import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, SvgIcon } from '@mui/material';
import { closeModal } from 'reducers/modalReducer';
import { setGenerateCodeError } from 'reducers/memberCodeStatusReducer';
import { setAccountSelectedProduct } from 'reducers/accountReducer';
import { saveTemporaryCaseThunk, setCaseId } from 'reducers/mandateReducer';
import { selectAccountNumber } from 'selectors/accountSelector';
import { LoadingButton } from 'components/common/LoadingButton';
import { selectMandateError, selectMandateLoading, selectMandateRequest } from 'selectors/mandateSelector';
import { CustomError } from 'types';
import { selectPartnerId } from 'selectors/partnerStatusSelector';
import NewTab from 'assets/icons/NewTab.svg?react';
import { selectUserFeatureToggles } from 'selectors/userFeatureToggleSelector';
import { useErrorHandling } from 'components/hooks/useErrorHandling';
import { setIsLoading } from 'reducers/mandateReducer';
import { AppDispatch, RootState } from 'store';
import { videoIdentificationStyles } from 'views/VideoIdentification/VideoIdentificationFormActions';
import { palette } from 'theme/palette';
import { useNavigate } from 'react-router';

export const DiscretionaryMandateActions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const partnerNumber = useSelector(selectPartnerId);
  const accountNumber = useSelector(selectAccountNumber);
  const error: CustomError | undefined = useSelector(selectMandateError);
  const requestBody = useSelector(selectMandateRequest);
  const isLoading = useSelector(selectMandateLoading);
  const features = useSelector(selectUserFeatureToggles);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  const handleSeeAvailableMandates = () => {
    if (requestBody) {
      dispatch(saveTemporaryCaseThunk(requestBody));
    }
  };

  const handleCancel = () => {
    dispatch(setIsLoading(false));
    if (partnerNumber) {
      navigate(`/partner?partnerNumber=${partnerNumber}`);
    } else if (accountNumber) {
      navigate(`/accounts?accountNumber=${accountNumber}`);
      dispatch(setAccountSelectedProduct(undefined));
      dispatch(setCaseId(undefined));
    }
  };

  useEffect(() => {
    if (requestBody?.caseId) {
      const url = `msd/offerings?create-for-case=${requestBody?.caseId}`;
      window.parent.postMessage({ message: 'redirectMSD', value: url }, CLM_URL);
      dispatch(setCaseId(undefined));
    }
  }, [requestBody, dispatch, CLM_URL]);

  useErrorHandling(error, () => {
    if (partnerNumber) {
      navigate(`/partner?partnerNumber=${partnerNumber}`);
    } else if (accountNumber) {
      navigate(`/accounts?accountNumber=${accountNumber}`);
    }
    dispatch(setGenerateCodeError(undefined));
    dispatch(closeModal());
  });

  return (
    <Box data-testid="discretionary-mandate-actions" sx={videoIdentificationStyles.actions}>
      <Box
        style={{
          width: '95%',
          display: 'flex',
          justifyContent: 'space-between',
          maxWidth: '95%',
        }}
      >
        <Button
          id="cancel-see-available-mandates-button"
          data-testid="cancel-see-available-mandates-button"
          onClick={handleCancel}
          variant="outlined"
          style={{ color: palette.black }}
        >
          Cancel
        </Button>
        <Box
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <LoadingButton
            style={{ marginRight: 0 }}
            id="see-available-mandates-button"
            data-testid="see-available-mandates-button"
            variant="contained"
            onClick={handleSeeAvailableMandates}
            color="primary"
            loading={isLoading}
            disabled={
              requestBody?.isRequiredNewBr === undefined ||
              (partnerNumber
                ? !features.PARTNER.CreateMandateProposal || !features.PARTNER.ViewMandateProposal
                : !features.BUSINESS_RELATION.CreateMandateProposal || !features.BUSINESS_RELATION.ViewMandateProposal)
            }
          >
            See available mandates (MSD)
            <SvgIcon style={{ padding: '1px', marginTop: '4px', marginLeft: '4px' }}>
              <NewTab />
            </SvgIcon>
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  );
};
